import { createContext, useCallback, useReducer } from "react";
import axios from "../../utils/axios/v1/adminAxios";

const GET_ENDPOINT_HISTORY = "GET_ENDPOINT_HISTORY";
const SET_ENDPOINT_STATUS = "SET_ENDPOINT_STATUS";
const SET_ENDPOINT_TOTAL = "SET_ENDPOINT_TOTAL";
const GET_CURRENT_ENDPOINT = "GET_CURRENT_ENDPOINT";
const GET_AUTH_INFO_HISTORY = "GET_AUTH_INFO_HISTORY";
const SET_AUTH_INFO_STATUS = "SET_AUTH_INFO_STATUS";
const SET_AUTH_INFO_TOTAL = "SET_AUTH_INFO_TOTAL";
const GET_CURRENT_AUTH_INFO = "GET_CURRENT_AUTH_INFO";
const GET_CLOUD_CONFIG = "GET_CLOUD_CONFIG";
const SET_ERROR = "SET_ERROR";

const initialState = {
  endpoints: null,
  endpointFrom: 0,
  endpointSize: 5,
  endpointTotal: 0,
  currentEndpoint: null,
  authInfos: null,
  authInfoFrom: 0,
  authInfoSize: 5,
  authInfoTotal: 0,
  currentAuthInfo: null,
  cloudEnabled: false,
  cloudConfigLoaded: false,
  errMsg: null,
};

const ClickHouseReducer = (state, action) => {
  switch (action.type) {
    case GET_ENDPOINT_HISTORY:
      return {
        ...state,
        endpoints: action.payload.endpoints,
      };
    case SET_ENDPOINT_STATUS:
      return {
        ...state,
        endpointFrom: action.payload.endpointFrom,
        endpointSize: action.payload.endpointSize,
      };
    case SET_ENDPOINT_TOTAL:
      return {
        ...state,
        endpointTotal: action.payload.endpointTotal,
      };
    case GET_CURRENT_ENDPOINT:
      return {
        ...state,
        currentEndpoint: action.payload.currentEndpoint,
      };
    case GET_AUTH_INFO_HISTORY:
      return {
        ...state,
        authInfos: action.payload.authInfos,
      };
    case SET_AUTH_INFO_STATUS:
      return {
        ...state,
        authInfoFrom: action.payload.authInfoFrom,
        authInfoSize: action.payload.authInfoSize,
      };
    case SET_AUTH_INFO_TOTAL:
      return {
        ...state,
        authInfoTotal: action.payload.authInfoTotal,
      };
    case GET_CURRENT_AUTH_INFO:
      return {
        ...state,
        currentAuthInfo: action.payload.currentAuthInfo,
      };
    case GET_CLOUD_CONFIG:
      return {
        ...state,
        cloudEnabled: action.payload.cloudEnabled,
        cloudConfigLoaded: true,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const ClickHouseContext = createContext(null);

function ClickHouseProvider({ children }) {
  const [state, dispatch] = useReducer(ClickHouseReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);

  const getCurrentEndpoint = useCallback(async () => {
    try {
      const response = await axios.get("clickhouse/ch_cloud_endpoint/current");
      dispatch({
        type: GET_CURRENT_ENDPOINT,
        payload: {
          currentEndpoint: response.data,
        },
      });
    } catch (err) {
      const errLower = (err.message || "").toLowerCase();
      if (!errLower.includes("record not found") && !errLower.includes("not found")) {
        setErr(err.message);
      }
      dispatch({
        type: GET_CURRENT_ENDPOINT,
        payload: {
          currentEndpoint: {},
        },
      });
    }
  }, [setErr]);

  const getEndpointHistory = useCallback(
    async (size, from, init = true) => {
      dispatch({
        type: GET_ENDPOINT_HISTORY,
        payload: {
          endpoints: null,
        },
      });
      dispatch({
        type: SET_ENDPOINT_STATUS,
        payload: {
          endpointSize: size,
          endpointFrom: from,
        },
      });
      if (init) {
        dispatch({
          type: SET_ENDPOINT_TOTAL,
          payload: {
            endpointTotal: 0,
          },
        });
      }
      try {
        const response = await axios.post("clickhouse/ch_cloud_endpoint/history", {
          from,
          size,
        });
        dispatch({
          type: GET_ENDPOINT_HISTORY,
          payload: {
            endpoints: response.data.data,
          },
        });
        dispatch({
          type: SET_ENDPOINT_TOTAL,
          payload: {
            endpointTotal: response.data.total,
          },
        });
      } catch (err) {
        const errLower = (err.message || "").toLowerCase();
        if (!errLower.includes("record not found") && !errLower.includes("not found")) {
          setErr(err.message);
        }
        dispatch({
          type: GET_ENDPOINT_HISTORY,
          payload: {
            endpoints: [],
          },
        });
        dispatch({
          type: SET_ENDPOINT_TOTAL,
          payload: {
            endpointTotal: 0,
          },
        });
      }
    },
    [setErr]
  );

  const updateEndpoint = useCallback(
    async (endpoint) => {
      try {
        await axios.put("clickhouse/ch_cloud_endpoint", { endpoint });
        getCurrentEndpoint();
        getEndpointHistory(state.endpointSize, 0);
      } catch (err) {
        setErr(err.message);
      }
    },
    [getCurrentEndpoint, getEndpointHistory, setErr, state.endpointSize]
  );

  const getCurrentAuthInfo = useCallback(async () => {
    try {
      const response = await axios.get("clickhouse/ch_auth_info/current");
      dispatch({
        type: GET_CURRENT_AUTH_INFO,
        payload: {
          currentAuthInfo: response.data,
        },
      });
    } catch (err) {
      const errLower = (err.message || "").toLowerCase();
      if (!errLower.includes("record not found") && !errLower.includes("not found")) {
        setErr(err.message);
      }
      dispatch({
        type: GET_CURRENT_AUTH_INFO,
        payload: {
          currentAuthInfo: {},
        },
      });
    }
  }, [setErr]);

  const getAuthInfoHistory = useCallback(
    async (size, from, init = true) => {
      dispatch({
        type: GET_AUTH_INFO_HISTORY,
        payload: {
          authInfos: null,
        },
      });
      dispatch({
        type: SET_AUTH_INFO_STATUS,
        payload: {
          authInfoSize: size,
          authInfoFrom: from,
        },
      });
      if (init) {
        dispatch({
          type: SET_AUTH_INFO_TOTAL,
          payload: {
            authInfoTotal: 0,
          },
        });
      }
      try {
        const response = await axios.post("clickhouse/ch_auth_info/history", {
          from,
          size,
        });
        dispatch({
          type: GET_AUTH_INFO_HISTORY,
          payload: {
            authInfos: response.data.data,
          },
        });
        dispatch({
          type: SET_AUTH_INFO_TOTAL,
          payload: {
            authInfoTotal: response.data.total,
          },
        });
      } catch (err) {
        const errLower = (err.message || "").toLowerCase();
        if (!errLower.includes("record not found") && !errLower.includes("not found")) {
          setErr(err.message);
        }
        dispatch({
          type: GET_AUTH_INFO_HISTORY,
          payload: {
            authInfos: [],
          },
        });
        dispatch({
          type: SET_AUTH_INFO_TOTAL,
          payload: {
            authInfoTotal: 0,
          },
        });
      }
    },
    [setErr]
  );

  const updateAuthInfo = useCallback(
    async (username, password) => {
      try {
        await axios.put("clickhouse/ch_auth_info", { username, password });
        getCurrentAuthInfo();
        getAuthInfoHistory(state.authInfoSize, 0);
      } catch (err) {
        setErr(err.message);
      }
    },
    [getCurrentAuthInfo, getAuthInfoHistory, setErr, state.authInfoSize]
  );

  const getCloudConfig = useCallback(async () => {
    try {
      const response = await axios.get("clickhouse/config");
      dispatch({
        type: GET_CLOUD_CONFIG,
        payload: {
          cloudEnabled: response.data.cloudEnabled ?? false,
        },
      });
    } catch (err) {
      setErr(err.response?.data?.msg || err.message);
      dispatch({
        type: GET_CLOUD_CONFIG,
        payload: {
          cloudEnabled: false,
        },
      });
    }
  }, [setErr]);

  const updateCloudConfig = useCallback(
    async (cloudEnabled) => {
      try {
        const response = await axios.put("clickhouse/config", { cloudEnabled });
        dispatch({
          type: GET_CLOUD_CONFIG,
          payload: {
            cloudEnabled: response.data.cloudEnabled ?? cloudEnabled,
          },
        });
        return true;
      } catch (err) {
        setErr(err.response?.data?.msg || err.message);
        return false;
      }
    },
    [setErr]
  );

  return (
    <ClickHouseContext.Provider
      value={{
        ...state,
        getEndpointHistory,
        updateEndpoint,
        getCurrentEndpoint,
        getAuthInfoHistory,
        updateAuthInfo,
        getCurrentAuthInfo,
        getCloudConfig,
        updateCloudConfig,
        setErr,
      }}
    >
      {children}
    </ClickHouseContext.Provider>
  );
}

export { ClickHouseContext, ClickHouseProvider };
